// ==========================================
// BB state machine (2d STATE MACHINE)
// ==========================================
//   dozing ──tap/hotkey──► idle
//   idle ──mic down──► listening ──release──► thinking
//   thinking ──token stream──► speaking (if TTS on) │ idle
//   any ──workflow ok──► happy (1.6s) ──► idle
//   happy ──milestone──► celebratory (2.4s) ──► idle
//   any ──error──► oops (2s) + recovery message ──► idle
//   idle ──5 min no input──► dozing
//
// Reactive states are self-expiring: `happy`, `celebratory` and `oops` each hold for
// a fixed beat and then fall back to idle on their own, so callers never have to
// remember to reset BB.
// ==========================================

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { BbState } from '../models/buddy.types';
import {
  BB_CELEBRATORY_MS,
  BB_DOZE_AFTER_MS,
  BB_HAPPY_MS,
  BB_OOPS_MS,
} from '../models/buddy.constants';

/** Accessibility labels — every expression is announced, colour never carries meaning alone. */
const STATE_LABELS: Readonly<Record<BbState, string>> = {
  idle: 'BB is idle',
  listening: 'BB is listening',
  thinking: 'BB is thinking',
  speaking: 'BB is speaking',
  happy: 'BB is pleased — task complete',
  celebratory: 'BB is celebrating a milestone',
  oops: 'BB hit a problem',
  dozing: 'BB is dozing',
};

@Injectable({ providedIn: 'root' })
export class BbStateService implements OnDestroy {
  private readonly state$ = new BehaviorSubject<BbState>('dozing');

  /** Timer that returns a transient expression to idle. */
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  /** Timer that drops BB into `dozing` after inactivity. */
  private dozeTimer: ReturnType<typeof setTimeout> | null = null;

  readonly state: Observable<BbState> = this.state$.pipe(distinctUntilChanged());

  get current(): BbState {
    return this.state$.value;
  }

  get label(): string {
    return STATE_LABELS[this.state$.value];
  }

  labelFor(state: BbState): string {
    return STATE_LABELS[state];
  }

  // ------------------------------------------
  // Transitions
  // ------------------------------------------

  /** dozing ──tap/hotkey──► idle. Also the generic "user did something" nudge. */
  wake(): void {
    if (this.current === 'dozing') {
      this.set('idle');
    }
    this.noteActivity();
  }

  /** idle ──mic down──► listening */
  listen(): void {
    this.set('listening');
  }

  /** listening ──release──► thinking */
  think(): void {
    this.set('thinking');
  }

  /** thinking ──token stream──► speaking, when TTS is on for the thread. */
  speak(): void {
    this.set('speaking');
  }

  /** Explicit return to rest — used when a stream finishes with TTS off. */
  settle(): void {
    // A transient expression owns BB until its own timer fires; don't cut it short.
    if (this.isTransient(this.current)) {
      return;
    }
    this.set('idle');
  }

  /** any ──workflow ok──► happy (1.6s) ──► idle */
  succeed(): void {
    this.hold('happy', BB_HAPPY_MS);
  }

  /** happy ──milestone──► celebratory (2.4s) ──► idle */
  celebrate(): void {
    this.hold('celebratory', BB_CELEBRATORY_MS);
  }

  /** any ──error──► oops (2s) ──► idle */
  fail(): void {
    this.hold('oops', BB_OOPS_MS);
  }

  /** idle ──5 min no input──► dozing */
  doze(): void {
    this.set('dozing');
  }

  /**
   * Resets the inactivity countdown. Called on every meaningful interaction so the
   * 5-minute doze only fires during genuine quiet.
   */
  noteActivity(): void {
    this.clearDozeTimer();
    this.dozeTimer = setTimeout(() => {
      // Only doze from rest — never interrupt a run, a stream or a celebration.
      if (this.current === 'idle') {
        this.set('dozing');
      }
    }, BB_DOZE_AFTER_MS);
  }

  // ------------------------------------------
  // Internals
  // ------------------------------------------

  private isTransient(state: BbState): boolean {
    return state === 'happy' || state === 'celebratory' || state === 'oops';
  }

  private hold(state: BbState, ms: number): void {
    this.set(state);
    this.clearHoldTimer();
    this.holdTimer = setTimeout(() => {
      this.holdTimer = null;
      // Guard against a newer transition having already replaced this one.
      if (this.current === state) {
        this.set('idle');
      }
    }, ms);
  }

  private set(next: BbState): void {
    if (next !== 'dozing') {
      this.clearDozeTimer();
    }
    if (!this.isTransient(next)) {
      this.clearHoldTimer();
    }
    this.state$.next(next);
    if (next === 'idle') {
      this.noteActivity();
    }
  }

  private clearHoldTimer(): void {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  private clearDozeTimer(): void {
    if (this.dozeTimer !== null) {
      clearTimeout(this.dozeTimer);
      this.dozeTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearHoldTimer();
    this.clearDozeTimer();
  }
}
